import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { Plus, Trash2, Edit2, Save, X, File, Download, Upload, Clock, CheckCircle, XCircle, AlertCircle, FileText, Eye, Tag, Settings, GripVertical, Copy, FileEdit, Search, LayoutGrid, List, ShieldQuestion, ThumbsUp, ThumbsDown, Scan, Sparkles, Loader2, Send, ClipboardList, FileArchive } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import DocumentViewer from './DocumentViewer';
import DocumentEditor from './DocumentEditor';
import StudentOfficialAuditModal from './StudentOfficialAuditModal';
import { Badge } from './ui';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface FileLabel {
  id: number;
  name: string;
  color: string | null;
  icon: string | null;
  description?: string | null;
  is_system?: boolean;
}

interface StudentFile {
  file_id: number;
  sophomorix_unid: string;
  file_type: 'private' | 'official';
  original_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  description: string | null;
  category: string | null;
  llm_summary: string | null;
  llm_processed_at: string | null;
  ocr_status: 'pending' | 'processing' | 'completed' | 'failed' | 'not_applicable' | null;
  processing_status?: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | null;
  processing_completed_at?: string | null;
  processing_error?: string | null;
  uploaded_by: number;
  created_at: string;
  updated_at: string;
  uploaded_by_username: string;
  uploaded_by_first_name: string | null;
  uploaded_by_last_name: string | null;
  queue_id: number | null;
  queue_status: 'submitted' | 'accepted' | 'rejected' | 'withdrawn' | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  queue_notes: string | null;
  reviewed_by_username: string | null;
  labels?: FileLabel[];
  keywords?: string[];
}

interface QueueAccessInfo {
  hasAccess: boolean;
  allowedClasses: string[] | null;
}

interface StudentFilesManagerProps {
  studentUnid: string;
  studentName: string;
  studentClass?: string;
}

function SortableGalleryCard({ id, children }: { id: number; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
      {children}
    </div>
  );
}

export default function StudentFilesManager({ studentUnid, studentName, studentClass }: StudentFilesManagerProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFileType, setUploadFileType] = useState<'private' | 'official'>('private');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [editingCategory, setEditingCategory] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [viewingFile, setViewingFile] = useState<StudentFile | null>(null);
  const [showLabelManager, setShowLabelManager] = useState(false);
  const [showLabelSelector, setShowLabelSelector] = useState<number | null>(null);
  const [showDocumentEditor, setShowDocumentEditor] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'gallery' | 'management'>('gallery'); // Default to gallery view
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);
  const [requestAccessComment, setRequestAccessComment] = useState('');
  const [showGrantModal, setShowGrantModal] = useState<{ requestId: number } | null>(null);
  const [showOfficialAuditModal, setShowOfficialAuditModal] = useState(false);
  const [grantExpiresDays, setGrantExpiresDays] = useState(30);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Debounce search: 500ms nach letztem Tastendruck erst suchen
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const normalizeClassName = (className?: string | null) => {
    if (!className) return '';
    return className.replace(/^0+/, '') || className;
  };

  // Fetch student files (full list) – nur wenn nicht gesucht wird
  const { data: studentFilesResponse, isLoading, error } = useQuery<{ data: StudentFile[]; can_see_official?: boolean; has_pending_access_request?: boolean }>(
    ['student-files', studentUnid],
    async () => {
      const response = await api.get(`/student-files/${studentUnid}`);
      return { data: response.data.data || [], can_see_official: response.data.can_see_official, has_pending_access_request: response.data.has_pending_access_request };
    },
    {
      enabled: !!studentUnid && debouncedSearchTerm.length < 2,
      retry: 3,
    }
  );
  const studentFilesRaw = studentFilesResponse?.data ?? [];
  const canSeeOfficial = studentFilesResponse?.can_see_official ?? true;
  const hasPendingAccessRequest = studentFilesResponse?.has_pending_access_request ?? false;

  // Volltextsuche über Backend (mit 500ms Debounce, 2+ Zeichen)
  const { data: searchResult, isLoading: searchLoading } = useQuery<{ data: Record<string, unknown>[] }>(
    ['student-files-search', studentUnid, debouncedSearchTerm],
    async () => {
      const response = await api.get('/student-files/search', {
        params: { q: debouncedSearchTerm, studentUnid, limit: 200 },
      });
      return response.data;
    },
    {
      enabled: !!studentUnid && debouncedSearchTerm.length >= 2,
      retry: 2,
    }
  );

  // Use search results when searching (2+ chars), otherwise full file list
  const studentFiles = useMemo((): StudentFile[] => {
    if (debouncedSearchTerm.length >= 2 && searchResult?.data) {
      return searchResult.data.map((r) => ({
        ...r,
        file_id: r.file_id as number,
        sophomorix_unid: (r.sophomorix_unid ?? studentUnid) as string,
        file_type: (r.file_type ?? 'private') as 'private' | 'official',
        original_name: (r.original_name ?? '') as string,
        file_path: (r.file_path ?? '') as string,
        file_size: (r.file_size ?? 0) as number,
        mime_type: (r.mime_type ?? '') as string,
        description: (r.description ?? null) as string | null,
        category: (r.category ?? null) as string | null,
        llm_summary: (r.llm_summary ?? null) as string | null,
        processing_status: (r.processing_status ?? null) as StudentFile['processing_status'],
        processing_completed_at: (r.processing_completed_at ?? null) as string | null,
        processing_error: (r.processing_error ?? null) as string | null,
        created_at: (r.created_at ?? '') as string,
        updated_at: (r.updated_at ?? r.created_at ?? '') as string,
        labels: (r.labels ?? []) as FileLabel[],
        keywords: (r.keywords ?? []) as string[],
        uploaded_by: (r.uploaded_by ?? 0) as number,
        uploaded_by_username: (r.uploaded_by_username ?? '') as string,
        uploaded_by_first_name: (r.uploaded_by_first_name ?? null) as string | null,
        uploaded_by_last_name: (r.uploaded_by_last_name ?? null) as string | null,
        queue_id: (r.queue_id ?? null) as number | null,
        queue_status: (r.queue_status ?? null) as string | null,
        submitted_at: (r.submitted_at ?? null) as string | null,
        reviewed_at: (r.reviewed_at ?? null) as string | null,
        rejection_reason: (r.rejection_reason ?? null) as string | null,
        queue_notes: (r.queue_notes ?? null) as string | null,
        reviewed_by_username: (r.reviewed_by_username ?? null) as string | null,
        llm_processed_at: (r.llm_processed_at ?? null) as string | null,
        ocr_status: (r.ocr_status ?? null) as StudentFile['ocr_status'],
      })) as StudentFile[];
    }
    return studentFilesRaw;
  }, [debouncedSearchTerm, searchResult, studentFilesRaw, studentUnid]);

  // Fetch labels
  const { data: labels = [], isLoading: labelsLoading } = useQuery<FileLabel[]>(
    ['student-file-labels'],
    async () => {
      try {
        const response = await api.get('/student-files/labels');
        return response.data.data || [];
      } catch (error) {
        console.error('Error fetching labels:', error);
        return [];
      }
    }
  );

  // Upload file mutation
  const uploadFileMutation = useMutation(
    async (formData: FormData) => {
      const response = await api.post(`/student-files/${studentUnid}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        setIsUploading(false);
        setSelectedFile(null);
        setUploadDescription('');
        setUploadCategory('');
        setUploadFileType('private');
        toast.success('Datei erfolgreich hochgeladen');
      },
      onError: (error: any) => {
        console.error('Error uploading file:', error);
        const message = error?.response?.data?.error || 'Fehler beim Hochladen der Datei';
        toast.error(message);
      },
    }
  );

  // Update file mutation
  const updateFileMutation = useMutation(
    async ({ fileId, description, category }: { fileId: number; description?: string; category?: string }) => {
      const response = await api.put(`/student-files/${fileId}`, { description, category });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        setEditingId(null);
        setEditingDescription('');
        setEditingCategory('');
        toast.success('Datei erfolgreich aktualisiert');
      },
      onError: (error: any) => {
        console.error('Error updating file:', error);
        const message = error?.response?.data?.error || 'Fehler beim Aktualisieren der Datei';
        toast.error(message);
      },
    }
  );

  // Assign label mutation
  const assignLabelMutation = useMutation(
    async ({ fileId, labelId }: { fileId: number; labelId: number }) => {
      await api.post(`/student-files/${fileId}/labels`, { label_id: labelId });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        toast.success('Label hinzugefügt');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Fehler beim Hinzufügen des Labels');
      },
    }
  );

  // Remove label mutation
  const removeLabelMutation = useMutation(
    async ({ fileId, labelId }: { fileId: number; labelId: number }) => {
      await api.delete(`/student-files/${fileId}/labels/${labelId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        toast.success('Label entfernt');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Fehler beim Entfernen des Labels');
      },
    }
  );

  // Reorder files mutation
  const reorderFilesMutation = useMutation(
    async (fileIds: number[]) => {
      await api.put('/student-files/reorder', { files: fileIds });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Fehler beim Sortieren');
      },
    }
  );

  // Move/Clone to official mutation
  const moveToOfficialMutation = useMutation(
    async ({ fileId, clone }: { fileId: number; clone: boolean }) => {
      const response = await api.post(`/student-files/${fileId}/move-to-official`, { clone });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        const cloned = data?.data?.cloned ?? false;
        toast.success(cloned ? 'Datei in offizielle Akte kopiert' : 'Datei in offizielle Akte verschoben');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || error?.response?.data?.message || 'Fehler beim Verschieben/Kopieren');
      },
    }
  );

  // Delete file mutation
  const deleteFileMutation = useMutation(
    async (fileId: number) => {
      const response = await api.delete(`/student-files/${fileId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        toast.success('Datei erfolgreich gelöscht');
      },
      onError: (error: any) => {
        console.error('Error deleting file:', error);
        const message = error?.response?.data?.error || 'Fehler beim Löschen der Datei';
        toast.error(message);
      },
    }
  );

  // Submit to queue mutation
  const submitToQueueMutation = useMutation(
    async (fileId: number) => {
      const response = await api.post(`/student-files/${fileId}/submit`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        toast.success('Datei zur offiziellen Akte eingereicht');
      },
      onError: (error: any) => {
        console.error('Error submitting file:', error);
        const message = error?.response?.data?.error || 'Fehler beim Einreichen der Datei';
        toast.error(message);
      },
    }
  );

  // Withdraw from queue mutation
  const withdrawFromQueueMutation = useMutation(
    async (queueId: number) => {
      const response = await api.post(`/student-files/queue/${queueId}/withdraw`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        toast.success('Datei aus der Warteschlange zurückgezogen');
      },
      onError: (error: any) => {
        console.error('Error withdrawing file:', error);
        const message = error?.response?.data?.error || 'Fehler beim Zurückziehen der Datei';
        toast.error(message);
      },
    }
  );

  // Accept queue item mutation
  const acceptQueueItemMutation = useMutation(
    async ({ queueId, notes }: { queueId: number; notes?: string }) => {
      const response = await api.post(`/student-files/queue/${queueId}/accept`, { notes });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        toast.success('Datei akzeptiert');
      },
      onError: (error: any) => {
        console.error('Error accepting file:', error);
        const message = error?.response?.data?.error || 'Fehler beim Akzeptieren der Datei';
        toast.error(message);
      },
    }
  );

  // Reject queue item mutation
  const rejectQueueItemMutation = useMutation(
    async ({ queueId, rejection_reason, notes }: { queueId: number; rejection_reason: string; notes?: string }) => {
      const response = await api.post(`/student-files/queue/${queueId}/reject`, { rejection_reason, notes });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        toast.success('Datei abgelehnt');
      },
      onError: (error: any) => {
        console.error('Error rejecting file:', error);
        const message = error?.response?.data?.error || 'Fehler beim Ablehnen der Datei';
        toast.error(message);
      },
    }
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      toast.error('Bitte wählen Sie eine Datei aus');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('file_type', uploadFileType);
    if (uploadDescription) formData.append('description', uploadDescription);
    if (uploadCategory) formData.append('category', uploadCategory);

    uploadFileMutation.mutate(formData);
  };

  const handleDocumentSave = (file: File, title: string, description: string, category: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', uploadFileType);
    if (description) formData.append('description', description);
    if (category) formData.append('category', category);

    uploadFileMutation.mutate(formData);
  };

  const handleEditFile = (file: StudentFile) => {
    setEditingId(file.file_id);
    setEditingDescription(file.description || '');
    setEditingCategory(file.category || '');
  };

  const handleSaveEdit = () => {
    if (editingId) {
      updateFileMutation.mutate({
        fileId: editingId,
        description: editingDescription || undefined,
        category: editingCategory || undefined,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingDescription('');
    setEditingCategory('');
  };

  const handleDeleteFile = (fileId: number) => {
    if (window.confirm('Möchten Sie diese Datei wirklich löschen?')) {
      deleteFileMutation.mutate(fileId);
    }
  };

  const handleDownload = (file: StudentFile) => {
    const base = api.defaults.baseURL || '/api';
    window.open(`${base}/student-files/${studentUnid}/download/${file.file_id}`, '_blank');
  };

  const handleExportAkte = () => {
    if (privateFiles.length === 0 && officialFiles.length === 0) {
      toast.error('Keine Dateien zum Exportieren vorhanden.');
      return;
    }
    const fileCount = privateFiles.length + officialFiles.length;
    const base = api.defaults.baseURL || '/api';
    const url = `${base}/student-files/${studentUnid}/export-zip`;
    window.open(url, '_blank');
    toast.success(`Schülerakte „${studentName}“: ${fileCount} Datei${fileCount !== 1 ? 'en' : ''} werden exportiert...`);
  };

  const handleView = (file: StudentFile) => {
    setViewingFile(file);
  };

  const handleSubmitToQueue = (fileId: number) => {
    if (window.confirm('Möchten Sie diese Datei zur offiziellen Akte einreichen? Sie können sie später nicht mehr bearbeiten.')) {
      submitToQueueMutation.mutate(fileId);
    }
  };

  const handleWithdraw = (queueId: number) => {
    if (window.confirm('Möchten Sie diese Datei aus der Warteschlange zurückziehen?')) {
      withdrawFromQueueMutation.mutate(queueId);
    }
  };

  const handleAcceptQueueItem = (queueId: number) => {
    const notes = window.prompt('Optional: Notizen hinzufügen:');
    acceptQueueItemMutation.mutate({ queueId, notes: notes || undefined });
  };

  const handleRejectQueueItem = (queueId: number) => {
    const rejection_reason = window.prompt('Bitte geben Sie einen Ablehnungsgrund ein:');
    if (!rejection_reason || rejection_reason.trim() === '') {
      toast.error('Ein Ablehnungsgrund ist erforderlich');
      return;
    }
    const notes = window.prompt('Optional: Zusätzliche Notizen:');
    rejectQueueItemMutation.mutate({ queueId, rejection_reason: rejection_reason.trim(), notes: notes || undefined });
  };

  // Request access to official files
  const requestAccessMutation = useMutation(
    async (comment?: string) => {
      const response = await api.post('/student-files/access-request', {
        sophomorix_unid: studentUnid,
        request_comment: comment || null,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files', studentUnid]);
        setShowRequestAccessModal(false);
        setRequestAccessComment('');
        toast.success('Zugriffsanfrage wurde gesendet. Schulleiter, Abteilungsleiter und Klassenlehrer wurden benachrichtigt.');
      },
      onError: (error: any) => {
        const msg = error?.response?.data?.error || 'Fehler beim Senden der Anfrage';
        toast.error(msg);
      },
    }
  );

  // Fetch queue access permissions (includes Klassenlehrer if configured)
  const { data: queueAccess } = useQuery<QueueAccessInfo>(
    ['student-files-queue-access'],
    async () => {
      const response = await api.get('/student-files/queue/access');
      return response.data.data;
    },
    {
      enabled: !!user,
      retry: 1
    }
  );

  const isAdminRole = user?.role === 'admin' || user?.role === 'schulleitung' || user?.role === 'abteilungsleiter';

  const hasQueueAccessForStudent = (() => {
    if (!queueAccess?.hasAccess) return false;
    if (queueAccess.allowedClasses === null) return true;
    if (!studentClass) return false;
    const normalizedStudentClass = normalizeClassName(studentClass);
    return queueAccess.allowedClasses.some((cls) => {
      const normalizedAllowed = normalizeClassName(cls);
      return cls === studentClass || normalizedAllowed === normalizedStudentClass;
    });
  })();

  // Pending access requests for this student (when user has queue access)
  const { data: pendingAccessForStudent = [], refetch: refetchPendingAccess } = useQuery<Array<{ id: number; requested_by_username: string; req_first: string; req_last: string; requested_at: string; request_comment: string | null }>>(
    ['student-files-access-requests', studentUnid],
    async () => {
      const response = await api.get('/student-files/access-requests/pending', { params: { sophomorix_unid: studentUnid } });
      return response.data.data || [];
    },
    { enabled: !!studentUnid && hasQueueAccessForStudent, retry: 1 }
  );

  const grantAccessMutation = useMutation(
    async ({ requestId, expires_at, expires_days }: { requestId: number; expires_at?: string; expires_days?: number }) => {
      await api.post(`/student-files/access-requests/${requestId}/grant`, { expires_at, expires_days });
    },
    { onSuccess: () => { queryClient.invalidateQueries(['student-files-access-requests', studentUnid]); queryClient.invalidateQueries(['student-files', studentUnid]); refetchPendingAccess(); toast.success('Zugriff gewährt'); }, onError: (e: any) => { toast.error(e?.response?.data?.error || 'Fehler'); } }
  );
  const denyAccessMutation = useMutation(
    async ({ requestId, denial_reason }: { requestId: number; denial_reason?: string }) => {
      await api.post(`/student-files/access-requests/${requestId}/deny`, { denial_reason });
    },
    { onSuccess: () => { queryClient.invalidateQueries(['student-files-access-requests', studentUnid]); refetchPendingAccess(); toast.success('Anfrage abgelehnt'); }, onError: (e: any) => { toast.error(e?.response?.data?.error || 'Fehler'); } }
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getQueueStatusBadge = (file: StudentFile) => {
    if (!file.queue_status) return null;

    switch (file.queue_status) {
      case 'submitted':
        return (
          <Badge variant="warning" className="gap-1 rounded px-2 py-1">
            <Clock className="h-3 w-3" />
            In Warteschlange
          </Badge>
        );
      case 'accepted':
        return (
          <Badge variant="success" className="gap-1 rounded px-2 py-1">
            <CheckCircle className="h-3 w-3" />
            Akzeptiert
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="danger" className="gap-1 rounded px-2 py-1">
            <XCircle className="h-3 w-3" />
            Abgelehnt
          </Badge>
        );
      case 'withdrawn':
        return (
          <Badge variant="neutral" className="gap-1 rounded px-2 py-1">
            <AlertCircle className="h-3 w-3" />
            Zurückgezogen
          </Badge>
        );
      default:
        return null;
    }
  };

  // Filter files: when using backend search (2+ chars), results are already filtered; otherwise client-side filter
  const filterBySearch = (file: StudentFile) => {
    if (!debouncedSearchTerm) return true;
    if (debouncedSearchTerm.length >= 2) return true; // Backend search already filtered
    const term = debouncedSearchTerm.toLowerCase();
    if (file.original_name?.toLowerCase().includes(term)) return true;
    if (file.description?.toLowerCase().includes(term)) return true;
    if (file.category?.toLowerCase().includes(term)) return true;
    if (file.llm_summary?.toLowerCase().includes(term)) return true;
    if (file.labels?.some(label => label.name.toLowerCase().includes(term))) return true;
    if (file.keywords?.some(keyword => keyword.toLowerCase().includes(term))) return true;
    return false;
  };

  const privateFiles = useMemo(() => 
    studentFiles.filter(f => f.file_type === 'private' && filterBySearch(f)),
    [studentFiles, debouncedSearchTerm]
  );
  
  const officialFiles = useMemo(() => 
    studentFiles.filter(f => f.file_type === 'official' && filterBySearch(f)),
    [studentFiles, debouncedSearchTerm]
  );

  // Drag & Drop sensors – activationConstraint: Klick ohne Bewegung öffnet Viewer statt Drag
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for private files
  const handlePrivateDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = privateFiles.findIndex(f => f.file_id === active.id);
    const newIndex = privateFiles.findIndex(f => f.file_id === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const newFiles = arrayMove(privateFiles, oldIndex, newIndex);
      const fileIds = newFiles.map(f => f.file_id);
      reorderFilesMutation.mutate(fileIds);
    }
  };

  // Handle drag end for official files
  const handleOfficialDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = officialFiles.findIndex(f => f.file_id === active.id);
    const newIndex = officialFiles.findIndex(f => f.file_id === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const newFiles = arrayMove(officialFiles, oldIndex, newIndex);
      const fileIds = newFiles.map(f => f.file_id);
      reorderFilesMutation.mutate(fileIds);
    }
  };

  // Nur beim initialen Laden den vollen Spinner – bei Suche Inhalte anzeigen und kleines Lade-Icon
  const isInitialLoading = isLoading && debouncedSearchTerm.length < 2;
  if (isInitialLoading) {
    return (
      <div className="card p-4">
        <div className="flex items-center justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600 dark:border-blue-400"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-300">Lade Schülerakte...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-red-200 p-4 dark:border-red-800">
        <div className="text-center text-red-600 dark:text-red-400">
          Fehler beim Laden der Schülerakte
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Upload Section */}
      <div className="card p-4">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center text-lg font-semibold text-gray-800 dark:text-gray-100">
            <FileText className="mr-2 h-5 w-5 text-blue-600 dark:text-blue-400" />
            Schülerakte
          </h3>
          <div className="flex items-center gap-2">
            {/* Search Input – Debounce 500ms, kleines Lade-Icon bei Suche */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field w-48 py-1.5 pl-8 pr-8 text-sm"
              />
              {debouncedSearchTerm.length >= 2 && searchLoading && (
                <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 dark:border-gray-600 dark:border-t-blue-400" />
                </div>
              )}
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {/* View Mode Toggle */}
            <div className="flex items-center overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
              <button
                onClick={() => setViewMode('gallery')}
                className={`p-1.5 ${
                  viewMode === 'gallery'
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-200'
                    : 'bg-white text-gray-600 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
                title="Galerie-Ansicht"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('management')}
                className={`p-1.5 ${
                  viewMode === 'management'
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-200'
                    : 'bg-white text-gray-600 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
                title="Verwaltungsansicht"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => setShowDocumentEditor(true)}
              className="btn btn-secondary btn-sm flex items-center"
            >
              <FileEdit className="h-4 w-4 mr-1" />
              Dokument erstellen
            </button>
            {(privateFiles.length > 0 || officialFiles.length > 0) && (
              <button
                onClick={handleExportAkte}
                className="btn btn-secondary btn-sm flex items-center"
                title={`Schülerakte „${studentName}“ als ZIP exportieren (${privateFiles.length + officialFiles.length} Dateien)`}
              >
                <FileArchive className="h-4 w-4 mr-1" />
                Export ({privateFiles.length + officialFiles.length})
              </button>
            )}
          <button
            onClick={() => setIsUploading(!isUploading)}
            className="btn btn-primary btn-sm flex items-center"
          >
            <Plus className="h-4 w-4 mr-1" />
            Datei hochladen
          </button>
          </div>
        </div>

        {/* Upload form */}
        {isUploading && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Datei auswählen</label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="input-field w-full py-1.5 text-sm"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.zip"
                />
                {selectedFile && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    Ausgewählt: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Typ</label>
                <select
                  value={uploadFileType}
                  onChange={(e) => setUploadFileType(e.target.value as 'private' | 'official')}
                  className="input-field w-full py-1.5 text-sm"
                >
                  <option value="private">Private Arbeitsakte (nur für Sie sichtbar)</option>
                  <option value="official">Offizielle Akte (für SL-Queue)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Kategorie (optional)</label>
                <input
                  type="text"
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  placeholder="z.B. Zeugnis, Elterngespräch, Förderplan"
                  className="input-field w-full py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Beschreibung (optional)</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Beschreibung der Datei"
                  className="input-field w-full py-1.5 text-sm"
                  rows={2}
                />
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleUpload}
                  disabled={uploadFileMutation.isLoading || !selectedFile}
                  className="btn btn-primary btn-sm flex items-center"
                >
                  {uploadFileMutation.isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-1" />
                      Hochladen
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsUploading(false);
                    setSelectedFile(null);
                    setUploadDescription('');
                    setUploadCategory('');
                    setUploadFileType('private');
                  }}
                  className="btn btn-secondary btn-sm flex items-center"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Gallery View - Default view for browsing documents */}
      {viewMode === 'gallery' && (
        <div className="card p-4">
          {/* All Files in Gallery View */}
          <div className="mb-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-md flex items-center font-semibold text-gray-800 dark:text-gray-100">
                <LayoutGrid className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400" />
                Alle Dokumente ({privateFiles.length + officialFiles.length})
              </h4>
              <div className="flex items-center gap-2">
                {(privateFiles.length > 0 || officialFiles.length > 0) && (
                  <button
                    onClick={handleExportAkte}
                    className="btn btn-secondary btn-sm flex items-center"
                    title={`Schülerakte „${studentName}“ als ZIP exportieren (${privateFiles.length + officialFiles.length} Dateien)`}
                  >
                    <FileArchive className="h-4 w-4 mr-1" />
                    Export ({privateFiles.length + officialFiles.length})
                  </button>
                )}
                {canSeeOfficial && (
                  <button
                    onClick={() => setShowOfficialAuditModal(true)}
                    className="btn btn-secondary btn-sm flex items-center"
                    title="Audit-Log aller offiziellen Dateien anzeigen"
                  >
                    <ClipboardList className="h-4 w-4 mr-1" />
                    Audit-Log
                  </button>
                )}
                <button
                  onClick={() => setViewMode('management')}
                  className="btn btn-secondary btn-sm flex items-center"
                  title="Zur Verwaltungsansicht wechseln"
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Verwalten
                </button>
              </div>
            </div>
            
            {/* Gallery Content */}
            {privateFiles.length === 0 && officialFiles.length === 0 && canSeeOfficial ? (
              <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                <FileText className="mx-auto mb-3 h-12 w-12 text-gray-300 dark:text-gray-500" />
                <p>Keine Dokumente vorhanden</p>
                <p className="text-sm mt-1">Laden Sie Dokumente hoch oder erstellen Sie ein neues Dokument.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Private Files Section */}
                {privateFiles.length > 0 && (
                  <div>
                    <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2 dark:border-gray-700">
                      <div className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 dark:bg-gray-800">
                        <File className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Private Arbeitsakte</span>
                        <span className="rounded-full bg-gray-600 px-2 py-0.5 text-xs text-white dark:bg-gray-500">{privateFiles.length}</span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Nur für Sie sichtbar</span>
                    </div>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePrivateDragEnd}>
                      <SortableContext items={privateFiles.map((f) => f.file_id)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                      {privateFiles.map((file) => (
                        <SortableGalleryCard key={file.file_id} id={file.file_id}>
                        <div
                          onClick={() => handleView(file)}
                          className="group relative cursor-pointer overflow-hidden rounded-lg border-2 border-gray-300 bg-gray-50 transition-all hover:border-gray-500 hover:shadow-md dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-400"
                        >
                          {file.uploaded_by === user?.id && (
                            <div className="absolute top-1 right-1 z-10">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleSubmitToQueue(file.file_id); }}
                                className="p-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white shadow"
                                title="Zur offiziellen Akte einreichen"
                              >
                                <Send className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                          {/* Thumbnail */}
                          <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 dark:bg-gray-700">
                            <img
                              src={`${api.defaults.baseURL}/student-files/thumbnail/${file.file_id}`}
                              alt={file.original_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className="absolute inset-0 hidden items-center justify-center bg-gray-100 dark:bg-gray-700">
                              <File className="h-12 w-12 text-gray-400" />
                            </div>
                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                              <Eye className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                          {/* File info */}
                          <div className="bg-gray-100 p-2 dark:bg-gray-800">
                            <p className="truncate text-xs font-medium text-gray-800 dark:text-gray-100" title={file.original_name}>
                              {file.original_name}
                            </p>
                            {file.llm_summary && (
                              <p className="mt-0.5 truncate text-[10px] text-gray-500 dark:text-gray-400" title={file.llm_summary}>
                                {file.llm_summary}
                              </p>
                            )}
                            {file.labels && file.labels.length > 0 && (
                              <div className="flex flex-wrap gap-0.5 mt-1">
                                {file.labels.slice(0, 2).map((label) => (
                                  <span
                                    key={label.id}
                                    className="text-[9px] px-1 py-0.5 rounded"
                                    style={{ backgroundColor: label.color || '#e5e7eb', color: '#374151' }}
                                  >
                                    {label.name}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* OCR & LLM Status – dezent */}
                            <div className="mt-1 flex items-center gap-1.5 border-t border-gray-200/60 pt-1 dark:border-gray-700">
                              {file.ocr_status && file.ocr_status !== 'not_applicable' && (
                                <span
                                  title={
                                    file.ocr_status === 'completed' ? 'OCR durchgeführt' :
                                    file.ocr_status === 'pending' || file.ocr_status === 'processing' ? 'OCR ausstehend' :
                                    file.ocr_status === 'failed' ? 'OCR fehlgeschlagen' : ''
                                  }
                                  className={`inline-flex items-center gap-0.5 text-[10px] ${
                                    file.ocr_status === 'completed' ? 'text-emerald-600/80' :
                                    file.ocr_status === 'pending' || file.ocr_status === 'processing' ? 'text-amber-500/70' :
                                    file.ocr_status === 'failed' ? 'text-red-500/70' : 'text-gray-400'
                                  }`}
                                >
                                  {file.ocr_status === 'processing' ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Scan className="h-3 w-3" />
                                  )}
                                </span>
                              )}
                              <span
                                title={file.llm_processed_at ? 'LLM-Analyse durchgeführt' : 'LLM-Analyse ausstehend'}
                                className={`inline-flex items-center gap-0.5 text-[10px] ${
                                  file.llm_processed_at ? 'text-emerald-600/80 dark:text-emerald-400/80' : 'text-gray-300 dark:text-gray-600'
                                }`}
                              >
                                <Sparkles className="h-3 w-3" />
                              </span>
                            </div>
                          </div>
                        </div>
                        </SortableGalleryCard>
                      ))}
                    </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
                
                {/* Offizielle Akte: Für Unberechtigte = Einsicht beantragen, sonst Dateien */}
                {!canSeeOfficial ? (
                  <div id="access-requests" className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-6 dark:border-amber-700 dark:bg-amber-950/30">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-start gap-3">
                        <ShieldQuestion className="h-10 w-10 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                        <div>
                          <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Offizielle Akte</h4>
                          <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">Sie haben keinen Zugriff auf die offizielle Akte dieses Schülers.</p>
                          {hasPendingAccessRequest ? (
                            <p className="mt-2 text-sm font-medium text-amber-700 dark:text-amber-300">Ihre Anfrage ist bereits eingereicht und wird bearbeitet.</p>
                          ) : (
                            <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">Schulleiter, Abteilungsleiter und Klassenlehrer können Ihnen befristeten Zugriff gewähren.</p>
                          )}
                        </div>
                      </div>
                      {!hasPendingAccessRequest && (
                        <button
                          onClick={() => setShowRequestAccessModal(true)}
                          className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-lg bg-amber-200 px-4 py-2 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-300 dark:bg-amber-900/50 dark:text-amber-200 dark:hover:bg-amber-900/70"
                        >
                          <ShieldQuestion className="h-4 w-4" />
                          Einsicht beantragen
                        </button>
                      )}
                    </div>
                  </div>
                ) : officialFiles.length > 0 ? (
                  <div>
                    <div className="mb-3 flex items-center gap-2 border-b border-green-200 pb-2 dark:border-green-800">
                      <div className="flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 dark:bg-green-950/50">
                        <FileText className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-700 dark:text-green-300">Offizielle Akte</span>
                        <span className="rounded-full bg-green-600 px-2 py-0.5 text-xs text-white dark:bg-green-700">{officialFiles.length}</span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Für Berechtigte sichtbar</span>
                    </div>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOfficialDragEnd}>
                      <SortableContext items={officialFiles.map((f) => f.file_id)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                      {officialFiles.map((file) => {
                        const needsReview = file.queue_status === 'submitted' && hasQueueAccessForStudent && file.queue_id;
                        return (
                        <SortableGalleryCard key={file.file_id} id={file.file_id}>
                        <div
                          onClick={() => handleView(file)}
                          className="group relative cursor-pointer overflow-hidden rounded-lg border-2 border-green-300 bg-green-50 transition-all hover:border-green-500 hover:shadow-md dark:border-green-700 dark:bg-green-950/30 dark:hover:border-green-500"
                        >
                          {needsReview && (
                            <div className="absolute top-1 right-1 z-10 flex gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleAcceptQueueItem(file.queue_id!); }}
                                className="p-1.5 rounded bg-green-600 hover:bg-green-700 text-white shadow"
                                title="Akzeptieren"
                              >
                                <ThumbsUp className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRejectQueueItem(file.queue_id!); }}
                                className="p-1.5 rounded bg-red-600 hover:bg-red-700 text-white shadow"
                                title="Ablehnen"
                              >
                                <ThumbsDown className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                          {file.queue_status === 'submitted' && (
                            <div className="absolute top-1 left-1 z-10">
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-white font-medium">Zur Prüfung</span>
                            </div>
                          )}
                          {/* Thumbnail */}
                          <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 dark:bg-gray-800">
                            <img
                              src={`${api.defaults.baseURL}/student-files/thumbnail/${file.file_id}`}
                              alt={file.original_name}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className="absolute inset-0 hidden items-center justify-center bg-gray-100 dark:bg-gray-800">
                              <FileText className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                            </div>
                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                              <Eye className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                          {/* File info */}
                          <div className="bg-green-100 p-2 dark:bg-green-950/50">
                            <p className="truncate text-xs font-medium text-gray-800 dark:text-gray-100" title={file.original_name}>
                              {file.original_name}
                            </p>
                            {file.llm_summary && (
                              <p className="mt-0.5 truncate text-[10px] text-gray-500 dark:text-gray-400" title={file.llm_summary}>
                                {file.llm_summary}
                              </p>
                            )}
                            {file.labels && file.labels.length > 0 && (
                              <div className="flex flex-wrap gap-0.5 mt-1">
                                {file.labels.slice(0, 2).map((label) => (
                                  <span
                                    key={label.id}
                                    className="text-[9px] px-1 py-0.5 rounded"
                                    style={{ backgroundColor: label.color || '#e5e7eb', color: '#374151' }}
                                  >
                                    {label.name}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* OCR & LLM Status – dezent */}
                            <div className="mt-1 flex items-center gap-1.5 border-t border-green-200/60 pt-1 dark:border-green-800/60">
                              {file.ocr_status && file.ocr_status !== 'not_applicable' && (
                                <span
                                  title={
                                    file.ocr_status === 'completed' ? 'OCR durchgeführt' :
                                    file.ocr_status === 'pending' || file.ocr_status === 'processing' ? 'OCR ausstehend' :
                                    file.ocr_status === 'failed' ? 'OCR fehlgeschlagen' : ''
                                  }
                                  className={`inline-flex items-center gap-0.5 text-[10px] ${
                                    file.ocr_status === 'completed' ? 'text-emerald-600/80 dark:text-emerald-400/80' :
                                    file.ocr_status === 'pending' || file.ocr_status === 'processing' ? 'text-amber-500/70 dark:text-amber-400/70' :
                                    file.ocr_status === 'failed' ? 'text-red-500/70 dark:text-red-400/70' : 'text-gray-400 dark:text-gray-500'
                                  }`}
                                >
                                  {file.ocr_status === 'processing' ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Scan className="h-3 w-3" />
                                  )}
                                </span>
                              )}
                              <span
                                title={file.llm_processed_at ? 'LLM-Analyse durchgeführt' : 'LLM-Analyse ausstehend'}
                                className={`inline-flex items-center gap-0.5 text-[10px] ${
                                  file.llm_processed_at ? 'text-emerald-600/80 dark:text-emerald-400/80' : 'text-gray-300 dark:text-gray-600'
                                }`}
                              >
                                <Sparkles className="h-3 w-3" />
                              </span>
                            </div>
                          </div>
                        </div>
                        </SortableGalleryCard>
                        );
                      })}
                    </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-900/50">
                    <FileText className="mx-auto mb-2 h-10 w-10 text-gray-400 dark:text-gray-500" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">Keine offiziellen Dateien vorhanden</p>
                  </div>
                )}

                {/* Zugriffsanfragen in Galerie: Gewähren/Ablehnen */}
                {hasQueueAccessForStudent && pendingAccessForStudent.length > 0 && (
                  <div id="access-requests" className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
                    <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                      <ShieldQuestion className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      Zugriffsanfragen ({pendingAccessForStudent.length})
                    </h4>
                    <div className="space-y-2">
                      {pendingAccessForStudent.map((req) => (
                        <div key={req.id} className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white p-3 dark:border-amber-800 dark:bg-gray-900">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {(req.req_first || req.req_last) ? `${req.req_first || ''} ${req.req_last || ''}`.trim() : req.requested_by_username}
                            </p>
                            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                              {req.request_comment || 'Keine Begründung'}
                            </p>
                          </div>
                          <div className="flex flex-shrink-0 gap-1">
                            <button
                              onClick={() => setShowGrantModal({ requestId: req.id })}
                              className="rounded-lg bg-green-100 p-2 text-green-700 hover:bg-green-200 dark:bg-green-950/50 dark:text-green-300 dark:hover:bg-green-950/70"
                              title="Gewähren"
                            >
                              <ThumbsUp className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                const reason = window.prompt('Optional: Ablehnungsgrund');
                                denyAccessMutation.mutate({ requestId: req.id, denial_reason: reason || undefined });
                              }}
                              className="rounded-lg bg-red-100 p-2 text-red-700 hover:bg-red-200 dark:bg-red-950/50 dark:text-red-300 dark:hover:bg-red-950/70"
                              title="Ablehnen"
                            >
                              <ThumbsDown className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Management View - For reordering, labels, etc. */}
      {viewMode === 'management' && (
        <>
      {/* Private Files Section */}
      <div className="card p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-md flex items-center font-semibold text-gray-800 dark:text-gray-100">
          <File className="mr-2 h-4 w-4 text-gray-600 dark:text-gray-400" />
          Private Arbeitsakte ({privateFiles.length})
        </h4>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('gallery')}
                  className="btn btn-secondary btn-sm flex items-center"
                  title="Zur Galerie-Ansicht wechseln"
                >
                  <LayoutGrid className="h-4 w-4 mr-1" />
                  Galerie
                </button>
                <button
                  onClick={() => setShowLabelManager(true)}
                  className="btn btn-secondary btn-sm flex items-center"
                  title="Labels verwalten"
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Labels
                </button>
              </div>
            </div>
            <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handlePrivateDragEnd}
        >
          <SortableContext
            items={privateFiles.map(f => f.file_id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
          {privateFiles.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Keine privaten Dateien vorhanden
            </div>
          ) : (
            privateFiles.map((file) => (
                  <SortableFileItem
                key={file.file_id}
                    file={file}
                    user={user}
                    isOfficial={false}
                    editingId={editingId}
                    editingCategory={editingCategory}
                    editingDescription={editingDescription}
                    setEditingCategory={setEditingCategory}
                    setEditingDescription={setEditingDescription}
                    handleSaveEdit={handleSaveEdit}
                    handleCancelEdit={handleCancelEdit}
                    updateFileMutation={updateFileMutation}
                    handleView={handleView}
                    handleDownload={handleDownload}
                    handleEditFile={handleEditFile}
                    moveToOfficialMutation={moveToOfficialMutation}
                    handleDeleteFile={handleDeleteFile}
                    removeLabelMutation={removeLabelMutation}
                    labels={labels}
                    setShowLabelSelector={setShowLabelSelector}
                    formatFileSize={formatFileSize}
                    formatDate={formatDate}
                    formatDateShort={formatDateShort}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Official Files Section */}
      <div id="official-files" className="card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-md flex items-center font-semibold text-gray-800 dark:text-gray-100">
            <FileText className="mr-2 h-4 w-4 text-green-600 dark:text-green-400" />
            Offizielle Akte ({canSeeOfficial ? officialFiles.length : '—'})
          </h4>
          {canSeeOfficial && (
            <button
              onClick={() => setShowOfficialAuditModal(true)}
              className="btn btn-secondary btn-sm flex items-center"
              title="Audit-Log aller offiziellen Dateien anzeigen"
            >
              <ClipboardList className="h-4 w-4 mr-1" />
              Audit-Log
            </button>
          )}
        </div>
        {!canSeeOfficial ? (
          <div id="access-requests" className="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-8 text-center dark:border-gray-600 dark:bg-gray-900/50">
            <ShieldQuestion className="mx-auto mb-3 h-10 w-10 text-amber-500 dark:text-amber-400" />
            <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">Sie haben keinen Zugriff auf die offizielle Akte dieses Schülers.</p>
            {hasPendingAccessRequest ? (
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Ihre Anfrage ist bereits eingereicht und wird bearbeitet.</p>
            ) : (
              <button
                onClick={() => setShowRequestAccessModal(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-100 px-4 py-2 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-200 dark:bg-amber-950/50 dark:text-amber-200 dark:hover:bg-amber-950/70"
              >
                <ShieldQuestion className="h-4 w-4" />
                Zugriff beantragen
              </button>
            )}
          </div>
        ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleOfficialDragEnd}
        >
          <SortableContext
            items={officialFiles.map(f => f.file_id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {officialFiles.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  Keine offiziellen Dateien vorhanden
                </div>
              ) : (
                officialFiles.map((file) => (
                  <SortableFileItem
                    key={file.file_id}
                    file={file}
                    editingId={editingId}
                    editingCategory={editingCategory}
                    editingDescription={editingDescription}
                    setEditingCategory={setEditingCategory}
                    setEditingDescription={setEditingDescription}
                    handleSaveEdit={handleSaveEdit}
                    handleCancelEdit={handleCancelEdit}
                    updateFileMutation={updateFileMutation}
                    handleView={handleView}
                    handleDownload={handleDownload}
                    handleEditFile={handleEditFile}
                    moveToOfficialMutation={null}
                    handleDeleteFile={handleDeleteFile}
                    removeLabelMutation={removeLabelMutation}
                    labels={labels}
                    setShowLabelSelector={setShowLabelSelector}
                    formatFileSize={formatFileSize}
                    formatDate={formatDate}
                    formatDateShort={formatDateShort}
                    getQueueStatusBadge={getQueueStatusBadge}
                    handleWithdraw={handleWithdraw}
                    handleAcceptQueueItem={handleAcceptQueueItem}
                    handleRejectQueueItem={handleRejectQueueItem}
                    hasQueueAccess={hasQueueAccessForStudent}
                    user={user}
                    isOfficial={true}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </DndContext>
        )}
      </div>

      {/* Pending Access Requests (for authorized users) */}
      {hasQueueAccessForStudent && pendingAccessForStudent.length > 0 && (
        <div id="access-requests" className="card p-4">
          <h4 className="text-md mb-3 flex items-center font-semibold text-gray-800 dark:text-gray-100">
            <ShieldQuestion className="mr-2 h-4 w-4 text-amber-600 dark:text-amber-400" />
            Zugriffsanfragen ({pendingAccessForStudent.length})
          </h4>
          <div className="space-y-2">
            {pendingAccessForStudent.map((req) => (
              <div key={req.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {(req.req_first || req.req_last) ? `${req.req_first || ''} ${req.req_last || ''}`.trim() : req.requested_by_username}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(req.requested_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {req.request_comment && ` · ${req.request_comment}`}
                  </p>
                </div>
                <div className="flex flex-shrink-0 gap-2">
                  <button
                    onClick={() => setShowGrantModal({ requestId: req.id })}
                    className="rounded bg-green-100 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-200 dark:bg-green-950/50 dark:text-green-300 dark:hover:bg-green-950/70"
                  >
                    Gewähren
                  </button>
                  <button
                    onClick={() => {
                      const reason = window.prompt('Optional: Ablehnungsgrund');
                      denyAccessMutation.mutate({ requestId: req.id, denial_reason: reason || undefined });
                    }}
                    className="rounded bg-red-100 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-200 dark:bg-red-950/50 dark:text-red-300 dark:hover:bg-red-950/70"
                  >
                    Ablehnen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
        </>
      )}

      {/* Grant Access Modal - außerhalb von viewMode für Galerie + Verwaltung */}
      {showGrantModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 dark:bg-black/70">
          <div className="mx-4 w-full max-w-sm rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40">
            <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Zugriff befristet gewähren</h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">Gültig für (Tage):</p>
            <input
              type="number"
              min={1}
              max={365}
              value={grantExpiresDays}
              onChange={(e) => setGrantExpiresDays(parseInt(e.target.value, 10) || 30)}
              className="input-field mb-4 w-full py-2"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowGrantModal(null)}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  grantAccessMutation.mutate({ requestId: showGrantModal.requestId, expires_days: grantExpiresDays });
                  setShowGrantModal(null);
                }}
                disabled={grantAccessMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Gewähren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Access Modal - außerhalb von viewMode, damit es in Galerie und Verwaltung funktioniert */}
      {showRequestAccessModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 dark:bg-black/70">
          <div className="mx-4 w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
              <ShieldQuestion className="h-5 w-5 text-amber-600" />
              Zugriff auf offizielle Akte beantragen
            </h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Schulleiter, Abteilungsleiter und Klassenlehrer werden benachrichtigt und können Ihren Zugriff befristet gewähren.
            </p>
            <textarea
              value={requestAccessComment}
              onChange={(e) => setRequestAccessComment(e.target.value)}
              placeholder="Optional: Begründung für die Zugriffsanfrage..."
              className="input-field mb-4 min-h-[80px] w-full py-2 text-sm"
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowRequestAccessModal(false); setRequestAccessComment(''); }}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Abbrechen
              </button>
              <button
                onClick={() => requestAccessMutation.mutate(requestAccessComment.trim() || undefined)}
                disabled={requestAccessMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {requestAccessMutation.isLoading ? 'Wird gesendet…' : 'Anfrage senden'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit-Log offizielle Akte – für berechtigte User */}
      <StudentOfficialAuditModal
        isOpen={showOfficialAuditModal}
        onClose={() => setShowOfficialAuditModal(false)}
        studentUnid={studentUnid}
        studentName={studentName}
      />

      {/* Document Viewer Modal */}
      {viewingFile && (
        <DocumentViewer
          isOpen={!!viewingFile}
          onClose={() => setViewingFile(null)}
          fileId={viewingFile.file_id}
          studentUnid={studentUnid}
          fileName={viewingFile.original_name}
          mimeType={viewingFile.mime_type}
          allFiles={studentFiles}
        />
      )}

      {/* Document Editor Modal */}
      <DocumentEditor
        isOpen={showDocumentEditor}
        onClose={() => setShowDocumentEditor(false)}
        onSave={handleDocumentSave}
        studentName={studentName}
        fileType={uploadFileType}
      />

      {/* Label Selector Modal */}
      {showLabelSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Label hinzufügen</h3>
              <button
                onClick={() => setShowLabelSelector(null)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {labels.filter(label => {
                const file = studentFiles.find(f => f.file_id === showLabelSelector);
                return !file?.labels?.some(l => l.id === label.id);
              }).map((label) => (
                <button
                  key={label.id}
                  onClick={() => {
                    if (showLabelSelector) {
                      assignLabelMutation.mutate({ fileId: showLabelSelector, labelId: label.id });
                      setShowLabelSelector(null);
                    }
                  }}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-left hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                  style={{
                    borderLeft: `3px solid ${label.color || '#6B7280'}`
                  }}
                >
                  <span className="font-medium">{label.name}</span>
                  {label.description && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">- {label.description}</span>
                  )}
                </button>
              ))}
              {labels.filter(label => {
                const file = studentFiles.find(f => f.file_id === showLabelSelector);
                return !file?.labels?.some(l => l.id === label.id);
              }).length === 0 && (
                <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  Alle verfügbaren Labels sind bereits zugewiesen
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Label Manager Modal */}
      {showLabelManager && (
        <LabelManagerModal
          isOpen={showLabelManager}
          onClose={() => setShowLabelManager(false)}
          labels={labels}
          onLabelsUpdated={() => {
            queryClient.invalidateQueries(['student-file-labels']);
            queryClient.invalidateQueries(['student-files', studentUnid]);
          }}
        />
      )}
    </div>
  );
}

// Sortable File Item Component
function SortableFileItem({
  file,
  editingId,
  editingCategory,
  editingDescription,
  setEditingCategory,
  setEditingDescription,
  handleSaveEdit,
  handleCancelEdit,
  updateFileMutation,
  handleView,
  handleDownload,
  handleEditFile,
  moveToOfficialMutation,
  handleDeleteFile,
  removeLabelMutation,
  labels,
  setShowLabelSelector,
  formatFileSize,
  formatDate,
  formatDateShort,
  getQueueStatusBadge,
  handleWithdraw,
  handleAcceptQueueItem,
  handleRejectQueueItem,
  hasQueueAccess,
  user,
  isOfficial = false
}: {
  file: StudentFile;
  editingId: number | null;
  editingCategory: string;
  editingDescription: string;
  setEditingCategory: (value: string) => void;
  setEditingDescription: (value: string) => void;
  handleSaveEdit: () => void;
  handleCancelEdit: () => void;
  updateFileMutation: any;
  handleView: (file: StudentFile) => void;
  handleDownload: (file: StudentFile) => void;
  handleEditFile: (file: StudentFile) => void;
  moveToOfficialMutation: any;
  handleDeleteFile: (fileId: number) => void;
  removeLabelMutation: any;
  labels: FileLabel[];
  setShowLabelSelector: (fileId: number) => void;
  formatFileSize: (bytes: number) => string;
  formatDate: (dateString: string) => string;
  formatDateShort: (dateString: string) => string;
  getQueueStatusBadge?: (file: StudentFile) => React.ReactNode;
  handleWithdraw?: (queueId: number) => void;
  handleAcceptQueueItem?: (queueId: number) => void;
  handleRejectQueueItem?: (queueId: number) => void;
  hasQueueAccess?: boolean;
  user?: any;
  isOfficial?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.file_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isAdminRole = user?.role === 'admin' || user?.role === 'schulleitung' || user?.role === 'abteilungsleiter';
  const canEditPrivate = !isOfficial && file.uploaded_by === user?.id;
  const canEditOfficial = isOfficial && (isAdminRole || file.uploaded_by === user?.id || hasQueueAccess);
  const canEditFile = canEditPrivate || canEditOfficial;
  const canDeleteFile = canEditFile;
  const canManageLabels = canEditFile;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative rounded-lg border-2 shadow-sm transition-all hover:shadow-md dark:shadow-black/20 ${
        isOfficial
          ? file.queue_status === 'accepted'
            ? 'border-green-300 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30'
            : 'border-yellow-300 bg-yellow-50/50 dark:border-amber-800 dark:bg-amber-950/30'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
      }`}
              >
                {editingId === file.file_id ? (
        <div className="space-y-2 p-3">
                    <input
                      type="text"
                      value={editingCategory}
                      onChange={(e) => setEditingCategory(e.target.value)}
                      placeholder="Kategorie"
                      className="input-field w-full py-1.5 text-sm"
                    />
                    <textarea
                      value={editingDescription}
                      onChange={(e) => setEditingDescription(e.target.value)}
                      placeholder="Beschreibung"
                      className="input-field w-full py-1.5 text-sm"
                      rows={2}
                    />
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={updateFileMutation.isLoading}
              className="btn btn-primary btn-sm flex items-center gap-1.5"
                      >
              <Save className="h-3.5 w-3.5" />
              Speichern
                      </button>
                      <button
                        onClick={handleCancelEdit}
              className="btn btn-secondary btn-sm flex items-center gap-1.5"
                      >
              <X className="h-3.5 w-3.5" />
              Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
          {/* Kompakte Hauptzeile - immer sichtbar */}
          <div className="flex items-center gap-3 p-3">
            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className="flex-shrink-0 cursor-grab text-gray-300 transition-colors active:cursor-grabbing group-hover:text-gray-500 dark:text-gray-600 dark:group-hover:text-gray-400"
              title="Zum Sortieren ziehen"
            >
              <GripVertical className="h-4 w-4" />
            </div>
            
            {/* Linksbündig: Datum, LLM Summary, Dateiname */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Datum - nur Tag/Monat/Jahr */}
              <span className="flex-shrink-0 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                {formatDateShort(file.created_at)}
              </span>
              
              {/* LLM Summary */}
              {file.llm_summary && (
                <span className="min-w-0 flex-1 truncate text-xs text-purple-600 dark:text-purple-400" title={file.llm_summary}>
                  🤖 {file.llm_summary}
                          </span>
                        )}
              {!file.llm_summary && file.processing_status && file.processing_status !== 'completed' && (
                <span
                  className={`min-w-0 flex-1 truncate text-xs ${
                    file.processing_status === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'
                  }`}
                  title={file.processing_error || 'Dokument wird verarbeitet'}
                >
                  {file.processing_status === 'failed' ? 'Verarbeitung fehlgeschlagen' : 'Dokument wird verarbeitet'}
                </span>
              )}
              
              {/* Dateiname */}
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <File className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                <span className="truncate font-medium text-gray-900 dark:text-gray-100">{file.original_name}</span>
                {/* Status Badge - nur für offizielle Akte */}
                {isOfficial && getQueueStatusBadge && getQueueStatusBadge(file)}
                      </div>
            </div>
            
            {/* Rechtsbündig: Labels */}
            {file.labels && file.labels.length > 0 && (
              <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                {file.labels.slice(0, 3).map((label) => (
                  <span
                    key={label.id}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
                    style={{
                      backgroundColor: label.color ? `${label.color}15` : '#f3f4f6',
                      color: label.color || '#6b7280',
                      border: `1px solid ${label.color ? `${label.color}40` : '#e5e7eb'}`
                    }}
                    title={label.name}
                  >
                    {label.name}
                  </span>
                ))}
                {file.labels.length > 3 && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">+{file.labels.length - 3}</span>
                )}
                    </div>
            )}
            
            {/* Quick Actions - nur bei Hover */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button
                onClick={() => handleView(file)}
                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-950/40 dark:hover:text-blue-400"
                title="Ansehen"
              >
                <Eye className="h-4 w-4" />
              </button>
                      <button
                        onClick={() => handleDownload(file)}
                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-950/40 dark:hover:text-green-400"
                        title="Herunterladen"
                      >
                        <Download className="h-4 w-4" />
                      </button>
              {canEditFile && (
                      <button
                        onClick={() => handleEditFile(file)}
                  className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                        title="Bearbeiten"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
              )}
            </div>

            {/* Löschknopf pro Zeile - immer sichtbar, wenn erlaubt */}
            {canDeleteFile && (
              <div className="flex items-center flex-shrink-0 ml-1">
                      <button
                        onClick={() => handleDeleteFile(file.file_id)}
                  className="rounded p-1.5 text-red-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                )}
              </div>

          {/* Erweiterte Informationen - nur bei Hover */}
          <div className="max-h-0 overflow-hidden px-3 pb-3 opacity-0 transition-all duration-200 group-hover:max-h-[500px] group-hover:opacity-100">
            <div className="space-y-2 border-t border-gray-100 pt-2 dark:border-gray-800">
              {/* Beschreibung */}
              {file.description && (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {file.description}
                </p>
              )}

              {/* Kategorie */}
                    {file.category && (
                <div className="inline-block">
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        {file.category}
                      </span>
                </div>
              )}

              {/* Alle Labels mit Remove-Button */}
              {file.labels && file.labels.length > 0 && canManageLabels && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {file.labels.map((label) => (
                    <span
                      key={label.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: label.color ? `${label.color}15` : '#f3f4f6',
                        color: label.color || '#6b7280',
                        border: `1px solid ${label.color ? `${label.color}40` : '#e5e7eb'}`
                      }}
                    >
                      {label.name}
                      <button
                        onClick={() => removeLabelMutation.mutate({ fileId: file.file_id, labelId: label.id })}
                        className="ml-0.5 hover:opacity-70 transition-opacity"
                        title="Label entfernen"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {canManageLabels && labels && labels.length > 0 && (
                    <button
                      onClick={() => setShowLabelSelector(file.file_id)}
                      className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60"
                      title="Label hinzufügen"
                    >
                      <Tag className="h-3 w-3" />
                      Label hinzufügen
                    </button>
                  )}
                  </div>
              )}

              {/* Rejection Reason */}
              {isOfficial && file.rejection_reason && (
                <div className="rounded-md border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-950/30">
                  <p className="text-xs text-red-700 dark:text-red-300">
                      <strong>Abgelehnt:</strong> {file.rejection_reason}
                    </p>
                </div>
              )}

              {/* Erweiterte Metadaten */}
              <div className="flex items-center gap-2 pt-1 text-xs text-gray-500 dark:text-gray-400">
                <span>{formatFileSize(file.file_size)}</span>
                <span>•</span>
                <span>{file.uploaded_by_username}</span>
                {isOfficial && file.reviewed_by_username && (
                  <>
                    <span>•</span>
                    <span className="font-medium text-green-600 dark:text-green-400">Geprüft von: {file.reviewed_by_username}</span>
                  </>
                )}
                </div>

              {/* Action Buttons */}
              {(() => {
                const hasMoveActions = !isOfficial && moveToOfficialMutation && canEditFile;
                const hasQueueActions = isOfficial && file.queue_status === 'submitted' && hasQueueAccess && handleAcceptQueueItem && handleRejectQueueItem && file.queue_id;
                const hasWithdrawAction = isOfficial && (file.queue_status === 'submitted' || file.queue_status === 'accepted') && file.uploaded_by === user?.id && handleWithdraw && file.queue_id;
                const hasDeleteAction = canDeleteFile;
                
                if (!hasMoveActions && !hasQueueActions && !hasWithdrawAction && !hasDeleteAction) {
                  return null;
                }
                
                return (
                  <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
                    {hasMoveActions && (
                      <>
                  <button
                          onClick={() => moveToOfficialMutation.mutate({ fileId: file.file_id, clone: false })}
                          className="btn btn-primary btn-sm flex items-center gap-1.5"
                  >
                          <Upload className="h-3.5 w-3.5" />
                          Verschieben
                  </button>
                        <button
                          onClick={() => moveToOfficialMutation.mutate({ fileId: file.file_id, clone: true })}
                          className="btn btn-secondary btn-sm flex items-center gap-1.5"
                        >
                          <Copy className="h-3.5 w-3.5" />
                          Kopieren
                        </button>
                      </>
                    )}
                    {hasQueueActions && (
                      <>
                        <button
                          onClick={() => handleAcceptQueueItem(file.queue_id!)}
                          className="btn btn-primary btn-sm flex items-center gap-1.5"
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                          Akzeptieren
                        </button>
                        <button
                          onClick={() => handleRejectQueueItem(file.queue_id!)}
                          className="btn btn-danger btn-sm flex items-center gap-1.5"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          Ablehnen
                        </button>
                      </>
                    )}
                    {hasWithdrawAction && (
                    <button
                      onClick={() => handleWithdraw(file.queue_id!)}
                        className="btn btn-warning btn-sm flex items-center gap-1.5"
                      >
                        <X className="h-3.5 w-3.5" />
                        Zurückziehen
                      </button>
                    )}
                    {hasDeleteAction && (
                      <button
                        onClick={() => handleDeleteFile(file.file_id)}
                        className="btn btn-danger btn-sm flex items-center gap-1.5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Löschen
                    </button>
                  )}
                </div>
                );
              })()}
              </div>
          </div>
        </>
          )}
        </div>
  );
}

// Label Manager Component
function LabelManagerModal({ isOpen, onClose, labels, onLabelsUpdated }: {
  isOpen: boolean;
  onClose: () => void;
  labels: FileLabel[];
  onLabelsUpdated: () => void;
}) {
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#6B7280');
  const queryClient = useQueryClient();

  const createLabelMutation = useMutation(
    async (data: { name: string; color: string }) => {
      await api.post('/student-files/labels', data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-file-labels']);
        setNewLabelName('');
        setNewLabelColor('#6B7280');
        toast.success('Label erstellt');
        onLabelsUpdated();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Fehler beim Erstellen des Labels');
      },
    }
  );

  const deleteLabelMutation = useMutation(
    async (labelId: number) => {
      await api.delete(`/student-files/labels/${labelId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-file-labels']);
        toast.success('Label gelöscht');
        onLabelsUpdated();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Fehler beim Löschen des Labels');
      },
    }
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70">
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Labels verwalten</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Create New Label */}
        <div className="mb-6 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
          <h4 className="mb-2 font-medium text-gray-900 dark:text-gray-100">Neues Label erstellen</h4>
          <div className="flex gap-2">
            <input
              type="text"
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
              placeholder="Label-Name"
              className="input input-sm flex-1"
            />
            <input
              type="color"
              value={newLabelColor}
              onChange={(e) => setNewLabelColor(e.target.value)}
              className="h-8 w-12 cursor-pointer rounded border border-gray-300 dark:border-gray-600"
            />
            <button
              onClick={() => {
                if (newLabelName.trim()) {
                  createLabelMutation.mutate({ name: newLabelName.trim(), color: newLabelColor });
                }
              }}
              disabled={!newLabelName.trim() || createLabelMutation.isLoading}
              className="btn btn-primary btn-sm"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Existing Labels */}
        <div className="space-y-2">
          <h4 className="mb-2 font-medium text-gray-900 dark:text-gray-100">Vorhandene Labels</h4>
          {labels.map((label) => (
            <div
              key={label.id}
              className="flex items-center justify-between p-3 rounded border"
              style={{
                borderLeft: `3px solid ${label.color || '#6B7280'}`
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: label.color || '#6B7280' }}
                />
                <span className="font-medium text-gray-900 dark:text-gray-100">{label.name}</span>
                {label.is_system && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">(System)</span>
                )}
              </div>
              {!label.is_system && (
                <button
                  onClick={() => {
                    if (confirm(`Label "${label.name}" wirklich löschen?`)) {
                      deleteLabelMutation.mutate(label.id);
                    }
                  }}
                  className="text-red-600 hover:text-red-800"
                  title="Label löschen"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {labels.length === 0 && (
                <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              Noch keine Labels vorhanden
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
